import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from 'sonner';

import {
  isRunningInNativeApp,
  native,
  useNavigateWithTransition,
} from '@lemoment/shared';

import { isForegroundSource } from './pushSources';
import { resolvePushRoute } from './usePushTap';

/**
 * Surface a foreground push as an in-app sonner toast — iOS silently
 * swallows the foreground banner, and Android skips the Notifee banner for
 * parity. Tapping the action routes via the same `redirectUrl` / `url`
 * resolution as a real push tap. No-op in a plain browser.
 */
export const useForegroundPushToast = (): void => {
  const navigate = useNavigateWithTransition();
  // react-i18next returns a fresh `t` on every language change; stash it in
  // a ref so the effect keeps stable deps and doesn't re-subscribe the
  // `pushReceived` listener on each locale flip.
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!isRunningInNativeApp()) {
      return;
    }

    return native.on('pushReceived', ({ data, source, title, body }) => {
      if (!isForegroundSource(source)) {
        return;
      }
      if (!title && !body) {
        return;
      }

      const route = resolvePushRoute(data);
      const action = route
        ? {
            label: tRef.current('push.open'),
            onClick: () => void navigate(route),
          }
        : undefined;

      toast(title ?? body ?? '', {
        description: title ? body : undefined,
        action,
      });
    });
  }, [navigate]);
};
